import { GetServerSidePropsContext } from "next/types"

import { mtLocation } from "@/lib/mt-location"
import { env } from "./env"

export function baseUrl(): string {
  const domainUrl = env("NEXT_PUBLIC_DOMAIN_URL")
  if (!domainUrl) {
    throw new Error("NEXT_PUBLIC_DOMAIN_URL is not set")
  }
  return domainUrl
}

export function tryParseUrl(
  url: string,
  options?:
    | { allowRelative?: boolean; relativeTo?: never }
    | { allowRelative?: never; relativeTo: string }
): URL | undefined {
  try {
    return parseUrl(url, options)
  } catch {
    return undefined
  }
}

export function parseUrl(
  url: string,
  options?:
    | { allowRelative?: boolean; relativeTo?: never }
    | { allowRelative?: never; relativeTo: string }
): URL {
  if (options?.relativeTo) {
    return new URL(url, options.relativeTo)
  } else if (options?.allowRelative && url.startsWith("/")) {
    return new URL(url, baseUrl())
  }

  return new URL(url)
}

export function isDomainMatch(url: string, domain: string): boolean {
  const urlObj = tryParseUrl(url)
  if (!urlObj) {
    return false
  }

  // Escape special regex characters in the domain pattern
  const escapedDomain = domain.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const regex = new RegExp(`(?:^|\\.)${escapedDomain}$`)

  return regex.test(urlObj.hostname)
}

export function convertNextUrlSearchParams(
  query: GetServerSidePropsContext["query"],
  options?: {
    baseParams?: URLSearchParams
    exclude?: (key: string, value: string | string[]) => boolean
    include?: (key: string, value: string | string[]) => boolean
  }
): URLSearchParams {
  const converted = options?.baseParams
    ? new URLSearchParams(options.baseParams)
    : new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value) {
      if (options?.exclude && options.exclude(key, value)) {
        continue
      }

      if (options?.include && !options.include(key, value)) {
        continue
      }

      if (Array.isArray(value)) {
        value.forEach((v) => converted.append(key, v))
      } else {
        converted.set(key, value)
      }
    }
  }

  return converted
}

export function removeParams(url: string): string {
  try {
    const urlObj = new URL(url)
    urlObj.search = ""
    urlObj.hash = ""
    return urlObj.toString()
  } catch {
    return url
  }
}

export function removePath(url: string): string {
  try {
    const urlObj = new URL(url)
    urlObj.pathname = ""
    urlObj.search = ""
    urlObj.hash = ""
    return urlObj.toString()
  } catch {
    return url
  }
}

export function isRelativeUrl(url: string | null | undefined): url is string {
  if (!url || typeof url !== "string") {
    return false
  }

  // Must start with "/" for relative path
  if (!url.startsWith("/")) {
    return false
  }

  // Cannot start with "//" (protocol-relative URLs)
  if (url.startsWith("//")) {
    return false
  }

  // Cannot contain protocol schemes
  if (/^\w+:/.test(url)) {
    return false
  }

  return true
}

export function currentPathUrlMatch(pathUrl: string) {
  return pathUrl === mtLocation.pathname + mtLocation.search
}

export function browserRedirect(pathUrl: string) {
  if (currentPathUrlMatch(pathUrl)) {
    mtLocation.reload()
  }
  mtLocation.replace(pathUrl)
}

/**
 * Decodes and validates a URL, returning a safe relative URL or fallback.
 * Use this for callback URLs from query params to prevent open redirect attacks.
 */
export function getSafeRedirectUrl(
  encodedUrl: string | null | undefined,
  fallback = "/"
): string {
  if (!encodedUrl) {
    return fallback
  }

  try {
    const decoded = decodeURIComponent(encodedUrl)
    return isRelativeUrl(decoded) ? decoded : fallback
  } catch {
    return fallback
  }
}

export function joinUrl(base: string, path: string, separator = "/"): string {
  return base.replace(new RegExp(`\\${separator}?$`), separator) + path
}

/**
 * Builds a URL with a callbackUrl query parameter.
 * Only includes the callback if it's a valid relative URL.
 */
export function buildUrlWithCallback(
  destination: string,
  callbackUrl: string
): string {
  if (!isRelativeUrl(callbackUrl)) {
    return destination
  }

  const params = new URLSearchParams({
    callbackUrl: encodeURIComponent(callbackUrl),
  })
  return `${destination}?${params.toString()}`
}

export function withMindtripUtm(url?: string): string {
  if (!url) return ""
  try {
    const u = new URL(url)
    u.searchParams.set("utm_source", "mindtrip")
    u.searchParams.set("utm_medium", "referral")
    return u.toString()
  } catch {
    return url
  }
}
